import React, { useEffect, memo, useMemo, useRef, useState } from "react";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, onSnapshot, serverTimestamp, updateDoc } from "firebase/firestore";
import { auth, db } from "../../utils/firebase";

import AnalysisPage from "./AnalysisPage";
import ArbitragePage from "./ArbitragePage";
import ValueBetsPage from "./ValueBetsPage";
import AnalysisStatsPage from "./AnalysisStatsPage";
import MiddlesPage from "./MiddlesPage";
import { getAnalysisTranslations } from "./analysisTranslate";

const MemoAnalysisPage    = memo(AnalysisPage);
const MemoArbitragePage   = memo(ArbitragePage);
const MemoValueBetsPage   = memo(ValueBetsPage);
const MemoMiddlesPage     = memo(MiddlesPage);
const MemoAnalysisStatsPage = memo(AnalysisStatsPage);

const LANG_KEY = "analysis_lang";
const ODDS_FORMAT_KEY = "analysis_options_odds_format";
const VALID_ODDS_FORMATS = ["hk", "decimal", "american"];

function readOddsFormat() {
  try {
    const v = safeGetLocalStorage(ODDS_FORMAT_KEY, "hk");
    const n = String(v || "").toLowerCase().trim();
    return VALID_ODDS_FORMATS.includes(n) ? n : "hk";
  } catch { return "hk"; }
}
const TIMEZONE_KEY = "analysis_time_zone";

const ANALYSIS_SESSION_ID_KEY = "analysis_session_id";
const ANALYSIS_SESSION_KICKED_KEY = "analysis_session_kicked_reason";
const ANALYSIS_LAST_ACTIVITY_AT_KEY = "analysis_last_activity_at";
const ANALYSIS_IDLE_TIMEOUT_MS = 20 * 60 * 1000;

function safeGetLocalStorage(key, fallback = "") {
  if (typeof window === "undefined") return fallback;
  try {
    const v = localStorage.getItem(key);
    return v == null ? fallback : v;
  } catch {
    return fallback;
  }
}

function safeSetLocalStorage(key, value) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(key, value);
  } catch {}
}

function safeRemoveLocalStorage(key) {
  if (typeof window === "undefined") return;
  try {
    localStorage.removeItem(key);
  } catch {}
}

function getLastActivityAt() {
  const raw = safeGetLocalStorage(ANALYSIS_LAST_ACTIVITY_AT_KEY, "");
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function setLastActivityNow() {
  safeSetLocalStorage(ANALYSIS_LAST_ACTIVITY_AT_KEY, String(Date.now()));
}

function isIdleExpired() {
  const last = getLastActivityAt();
  if (!last) return false;
  return Date.now() - last >= ANALYSIS_IDLE_TIMEOUT_MS;
}

function applyLangToDocument(lang) {
  if (typeof document === "undefined") return;
  document.documentElement.setAttribute("lang", lang);
}

function broadcastLangChange(lang) {
  if (typeof window === "undefined") return;
  try {
    window.__analysisLang = lang;
    window.dispatchEvent(
      new CustomEvent("analysis-language-change", {
        detail: { lang }
      })
    );
  } catch {}
}

function getBrowserTimeZone() {
  if (typeof window === "undefined") return "";
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
}

function normalizeTimeZone(timeZone) {
  if (typeof timeZone !== "string") return "";
  return timeZone.trim();
}

function getInitialTimeZone() {
  const saved = normalizeTimeZone(safeGetLocalStorage(TIMEZONE_KEY, ""));
  if (saved) return saved;

  const browserTz = normalizeTimeZone(getBrowserTimeZone());
  if (browserTz) return browserTz;

  return "";
}

function normalizePlanFromClaims(raw) {
  const p = typeof raw === "string" ? raw.trim().toLowerCase() : "";
  if (p === "basic" || p === "pro" || p === "premium") return p;
  return "basic";
}

function getPlanBadgeConfig(plan) {
  const p = typeof plan === "string" ? plan.trim().toLowerCase() : "basic";

  if (p === "pro") {
    return {
      label: "Pro",
      color: "#a8c4ff"
    };
  }

  if (p === "premium") {
    return {
      label: "Premium",
      color: "#f2d878"
    };
  }

  return {
    label: "Basic",
    color: "#9aa5b8"
  };
}

const LOCKED_UPGRADE_TO_PRO_EN = "You need to upgrade to Pro to unlock this feature.";
const LOCKED_UPGRADE_TO_PREMIUM_EN =
  "You need to upgrade to Premium to unlock this feature.";

function getLocalizedLockedUpgradeMessage(kind, lang) {
  const l = typeof lang === "string" ? lang.trim() : "";

  const messagesByLang = {
    "ja-JP": {
      pro: "この機能を利用するにはProにアップグレードする必要があります。",
      premium: "この機能を利用するにはPremiumにアップグレードする必要があります。"
    },
    "it-IT": {
      pro: "Per sbloccare questa funzione, devi passare a Pro.",
      premium: "Per sbloccare questa funzione, devi passare a Premium."
    },
    "es-ES": {
      pro: "Necesitas actualizar a Pro para desbloquear esta función.",
      premium: "Necesitas actualizar a Premium para desbloquear esta función."
    },
    "ru-RU": {
      pro: "Чтобы разблокировать эту функцию, нужно перейти на Pro.",
      premium: "Чтобы разблокировать эту функцию, нужно перейти на Premium."
    },
    "fr-FR": {
      pro: "Vous devez passer à la formule Pro pour débloquer cette fonctionnalité.",
      premium: "Vous devez passer à la formule Premium pour débloquer cette fonctionnalité."
    },
    "de-DE": {
      pro: "Um diese Funktion freizuschalten, musst du auf Pro upgraden.",
      premium: "Um diese Funktion freizuschalten, musst du auf Premium upgraden."
    },
    "en-GB": {
      pro: LOCKED_UPGRADE_TO_PRO_EN,
      premium: LOCKED_UPGRADE_TO_PREMIUM_EN
    },
    "tr-TR": {
      pro: "Bu özelliğin kilidini açmak için Pro'ya yükseltmeniz gerekir.",
      premium: "Bu özelliğin kilidini açmak için Premium'a yükseltmeniz gerekir."
    },
    "zh-CN": {
      pro: "要解锁此功能，你需要升级到 Pro。",
      premium: "要解锁此功能，你需要升级到 Premium。"
    },
    "th-TH": {
      pro: "หากต้องการปลดล็อกฟีเจอร์นี้ คุณต้องอัปเกรดเป็น Pro",
      premium: "หากต้องการปลดล็อกฟีเจอร์นี้ คุณต้องอัปเกรดเป็น Premium"
    },
    "vi-VN": {
      pro: "Để mở khóa tính năng này, bạn cần nâng cấp lên Pro.",
      premium: "Để mở khóa tính năng này, bạn cần nâng cấp lên Premium."
    },
    "ko-KR": {
      pro: "이 기능을 사용하려면 Pro로 업그레이드해야 합니다.",
      premium: "이 기능을 사용하려면 Premium으로 업그레이드해야 합니다."
    },
    "en-SG": {
      pro: LOCKED_UPGRADE_TO_PRO_EN,
      premium: LOCKED_UPGRADE_TO_PREMIUM_EN
    },
    "id-ID": {
      pro: "Untuk membuka fitur ini, Anda perlu upgrade ke Pro.",
      premium: "Untuk membuka fitur ini, Anda perlu upgrade ke Premium."
    },
    "nl-NL": {
      pro: "Je moet upgraden naar Pro om deze functie te ontgrendelen.",
      premium: "Je moet upgraden naar Premium om deze functie te ontgrendelen."
    }
  };

  const entry = messagesByLang[l] || messagesByLang["en-GB"];
  if (!entry) {
    return kind === "premium" ? LOCKED_UPGRADE_TO_PREMIUM_EN : LOCKED_UPGRADE_TO_PRO_EN;
  }

  return kind === "premium" ? entry.premium : entry.pro;
}

function canAccessSteam(plan) {
  return (typeof plan === "string" ? plan : "").trim().toLowerCase() === "premium";
}

function canAccessArb() {
  return true;
}

function canAccessMiddles() {
  return true;
}

function canAccessValue(plan) {
  const p = (typeof plan === "string" ? plan : "").trim().toLowerCase();
  return p === "pro" || p === "premium";
}

function canAccessNotes() {
  return true;
}

function LockedFeaturePanel({ message }) {
  return (
    <div
      style={{
        minHeight: "55vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px"
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "620px",
          borderRadius: "18px",
          padding: "22px 18px",
          background:
            "radial-gradient(circle at top left, rgba(76, 111, 255, .14), transparent 55%), radial-gradient(circle at bottom right, rgba(124, 109, 255, .10), transparent 60%), rgba(17, 31, 66, .96)",
          border: "1px solid rgba(180, 197, 235, 0.16)",
          boxShadow:
            "0 22px 70px rgba(0,0,0,0.32), inset 0 1px 0 rgba(180,197,235,0.08), inset 0 0 0 1px rgba(0,0,0,0.28)",
          backdropFilter: "blur(6px)"
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "14px"
          }}
        >
          <div
            aria-hidden="true"
            style={{
              width: "46px",
              height: "46px",
              borderRadius: "14px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              background: "rgba(76, 111, 255, 0.18)",
              border: "1px solid rgba(180, 197, 235, 0.16)"
            }}
          >
            <svg
              width="22"
              height="22"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7.5 10V7.7C7.5 5.1 9.4 3 12 3C14.6 3 16.5 5.1 16.5 7.7V10"
                stroke="rgba(244, 247, 255, .88)"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
              <path
                d="M6.7 10H17.3C18.2 10 19 10.8 19 11.7V19C19 20.1 18.1 21 17 21H7C5.9 21 5 20.1 5 19V11.7C5 10.8 5.8 10 6.7 10Z"
                stroke="rgba(244, 247, 255, .88)"
                strokeWidth="1.6"
                strokeLinejoin="round"
              />
              <path
                d="M12 14.2V16.6"
                stroke="rgba(76, 111, 255, .95)"
                strokeWidth="1.6"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <div
            style={{
              fontSize: "14px",
              lineHeight: 1.55,
              color: "#a8b3cf",
              fontWeight: 500
            }}
          >
            {message}
          </div>
        </div>
      </div>
    </div>
  );
}

async function readUserPlanFromToken(user) {
  try {
    const tokenResult = await user.getIdTokenResult();
    const raw = tokenResult?.claims?.plan;
    return normalizePlanFromClaims(raw);
  } catch {
    return "basic";
  }
}

function FlagIcon({ item, size = 16 }) {
  const [imgError, setImgError] = useState(false);

  if (!imgError && item.flagUrl) {
    return (
      <img
        src={item.flagUrl}
        alt={item.label}
        width={size + 6}
        height={Math.round((size + 6) * 0.72)}
        onError={() => setImgError(true)}
        style={{
          display: "block",
          width: `${size + 6}px`,
          height: `${Math.round((size + 6) * 0.72)}px`,
          objectFit: "cover",
          borderRadius: "2px",
          flexShrink: 0,
          boxShadow: "0 0 0 1px rgba(180, 197, 235, 0.10)"
        }}
      />
    );
  }

  return (
    <span
      style={{
        width: `${size + 6}px`,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: `${size}px`,
        lineHeight: 1,
        flexShrink: 0
      }}
    >
      {item.flag}
    </span>
  );
}

export default function AnalysisLayout() {
  const [userEmail, setUserEmail] = useState("");
  const [userPlan, setUserPlan] = useState("basic");
  const [tab, setTab] = useState("steam");
  const [valueTabNotificationCount, setValueTabNotificationCount] = useState(0);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const langMenuRef = useRef(null);
  const [currentUser, setCurrentUser] = useState(null);
  const sessionUnsubRef = useRef(null);
  const idleTimerRef = useRef(null);
  const lastActivityWriteRef = useRef(0);
  const idleLogoutStartedRef = useRef(false);

  const languageOptions = useMemo(
    () => [
      {
        code: "ja-JP",
        label: "Japanese",
        flag: "🇯🇵",
        flagUrl: "https://flagcdn.com/w40/jp.png"
      },
      {
        code: "it-IT",
        label: "Italian",
        flag: "🇮🇹",
        flagUrl: "https://flagcdn.com/w40/it.png"
      },
      {
        code: "es-ES",
        label: "Spanish",
        flag: "🇪🇸",
        flagUrl: "https://flagcdn.com/w40/es.png"
      },
      {
        code: "ru-RU",
        label: "Russian",
        flag: "🇷🇺",
        flagUrl: "https://flagcdn.com/w40/ru.png"
      },
      {
        code: "fr-FR",
        label: "French",
        flag: "🇫🇷",
        flagUrl: "https://flagcdn.com/w40/fr.png"
      },
      {
        code: "de-DE",
        label: "German",
        flag: "🇩🇪",
        flagUrl: "https://flagcdn.com/w40/de.png"
      },
      {
        code: "en-GB",
        label: "English",
        flag: "🇬🇧",
        flagUrl: "https://flagcdn.com/w40/gb.png"
      },
      {
        code: "tr-TR",
        label: "Turkish",
        flag: "🇹🇷",
        flagUrl: "https://flagcdn.com/w40/tr.png"
      },
      {
        code: "zh-CN",
        label: "Chinese",
        flag: "🇨🇳",
        flagUrl: "https://flagcdn.com/w40/cn.png"
      },
      {
        code: "th-TH",
        label: "Thai",
        flag: "🇹🇭",
        flagUrl: "https://flagcdn.com/w40/th.png"
      },
      {
        code: "vi-VN",
        label: "Vietnamese",
        flag: "🇻🇳",
        flagUrl: "https://flagcdn.com/w40/vn.png"
      },
      {
        code: "ko-KR",
        label: "Korean",
        flag: "🇰🇷",
        flagUrl: "https://flagcdn.com/w40/kr.png"
      },
      {
        code: "en-SG",
        label: "Singapore",
        flag: "🇸🇬",
        flagUrl: "https://flagcdn.com/w40/sg.png"
      },
      {
        code: "id-ID",
        label: "Indonesian",
        flag: "🇮🇩",
        flagUrl: "https://flagcdn.com/w40/id.png"
      },
      {
        code: "nl-NL",
        label: "Dutch",
        flag: "🇳🇱",
        flagUrl: "https://flagcdn.com/w40/nl.png"
      }
    ],
    []
  );

  const [lang, setLang] = useState(() => {
    return safeGetLocalStorage(LANG_KEY, "en-GB");
  });

  const [timeZone, setTimeZone] = useState(() => getInitialTimeZone());
  const [oddsFormat, setOddsFormat] = useState(() => readOddsFormat());

  const t = useMemo(() => getAnalysisTranslations(lang), [lang]);

  const currentLanguage = useMemo(() => {
    return (
      languageOptions.find((item) => item.code === lang) ||
      languageOptions.find((item) => item.code === "en-GB") ||
      languageOptions[0]
    );
  }, [lang, languageOptions]);

  useEffect(() => {
    applyLangToDocument(lang);
    safeSetLocalStorage(LANG_KEY, lang);
    broadcastLangChange(lang);
  }, [lang]);

  useEffect(() => {
    const resolvedTimeZone = normalizeTimeZone(timeZone) || normalizeTimeZone(getBrowserTimeZone());
    if (!resolvedTimeZone) return;

    setTimeZone((prev) => (prev === resolvedTimeZone ? prev : resolvedTimeZone));
    safeSetLocalStorage(TIMEZONE_KEY, resolvedTimeZone);
  }, [timeZone]);

  useEffect(() => {
    function sync() {
      const next = readOddsFormat();
      setOddsFormat((prev) => (prev !== next ? next : prev));
    }
    window.addEventListener("storage", sync);
    window.addEventListener("analysis_odds_format_changed", sync);
    const iv = setInterval(sync, 300);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("analysis_odds_format_changed", sync);
      clearInterval(iv);
    };
  }, []);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      if (!user) {
        setCurrentUser(null);
        if (sessionUnsubRef.current) {
          try { sessionUnsubRef.current(); } catch {}
          sessionUnsubRef.current = null;
        }
        if (idleTimerRef.current) {
          clearTimeout(idleTimerRef.current);
          idleTimerRef.current = null;
        }
        window.location = "/analysis/login";
      } else {
        setUserEmail(user.email || "");
        readUserPlanFromToken(user).then(setUserPlan);
        setCurrentUser(user);
      }
    });

    return () => unsub();
  }, []);

  useEffect(() => {
    if (!currentUser || !db) {
      if (sessionUnsubRef.current) {
        try { sessionUnsubRef.current(); } catch {}
        sessionUnsubRef.current = null;
      }
      return;
    }

    const localSessionId = safeGetLocalStorage(ANALYSIS_SESSION_ID_KEY, "").trim();
    if (!localSessionId) {
      safeSetLocalStorage(ANALYSIS_SESSION_KICKED_KEY, "session_invalid");
      signOut(auth).catch(() => {});
      window.location = "/analysis/login";
      return;
    }

    const sessionDocRef = doc(db, "analysisSessions", currentUser.uid);

    const unsub = onSnapshot(
      sessionDocRef,
      (snap) => {
        if (!snap.exists()) {
          safeSetLocalStorage(ANALYSIS_SESSION_KICKED_KEY, "session_invalid");
          signOut(auth).catch(() => {});
          window.location = "/analysis/login";
          return;
        }
        const data = snap.data();
        const activeSessionId = data?.activeSessionId;
        if (activeSessionId && activeSessionId !== localSessionId) {
          safeSetLocalStorage(ANALYSIS_SESSION_KICKED_KEY, "other_device");
          signOut(auth).catch(() => {});
          window.location = "/analysis/login";
        }
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.error("[AnalysisLayout session listener]", err);
      }
    );

    sessionUnsubRef.current = unsub;

    return () => {
      try { unsub(); } catch {}
      sessionUnsubRef.current = null;
    };
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;

    const uid = currentUser.uid;

    const handleIdle = async () => {
      if (idleLogoutStartedRef.current) return;
      idleLogoutStartedRef.current = true;
      safeSetLocalStorage(ANALYSIS_SESSION_KICKED_KEY, "idle_timeout");
      safeRemoveLocalStorage(ANALYSIS_SESSION_ID_KEY);
      safeRemoveLocalStorage(ANALYSIS_LAST_ACTIVITY_AT_KEY);
      try { await signOut(auth); } catch {}
      window.location = "/analysis/login";
    };

    const resetIdleTimer = () => {
      if (isIdleExpired()) {
        void handleIdle();
        return;
      }
      setLastActivityNow();

      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(handleIdle, ANALYSIS_IDLE_TIMEOUT_MS);

      const now = Date.now();
      if (db && now - lastActivityWriteRef.current > 60000) {
        lastActivityWriteRef.current = now;
        const localSessionId = safeGetLocalStorage(ANALYSIS_SESSION_ID_KEY, "").trim();
        if (localSessionId) {
          updateDoc(doc(db, "analysisSessions", uid), {
            lastActivityAt: serverTimestamp()
          }).catch(() => {});
        }
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        if (isIdleExpired()) {
          void handleIdle();
        } else {
          resetIdleTimer();
        }
      }
    };

    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];
    events.forEach((ev) => window.addEventListener(ev, resetIdleTimer, { passive: true }));
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // On mount: seed timestamp if missing, or immediately logout if already expired
    if (!getLastActivityAt()) {
      setLastActivityNow();
    } else if (isIdleExpired()) {
      void handleIdle();
      return () => {
        events.forEach((ev) => window.removeEventListener(ev, resetIdleTimer));
        document.removeEventListener("visibilitychange", handleVisibilityChange);
        if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      };
    }

    resetIdleTimer();

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, resetIdleTimer));
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [currentUser]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!langMenuRef.current) return;
      if (!langMenuRef.current.contains(event.target)) {
        setLangMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    safeRemoveLocalStorage(ANALYSIS_SESSION_ID_KEY);
    safeRemoveLocalStorage(ANALYSIS_LAST_ACTIVITY_AT_KEY);
    await signOut(auth);
    window.location = "/analysis/login";
  };

  const handleSelectLanguage = (code) => {
    setLang(code);
    setLangMenuOpen(false);
  };

  const tabs = useMemo(() => [
    { key: "steam", icon: "📈", text: t.tabs.steam },
    {
      key: "value",
      icon: "💰",
      text: t.tabs.value,
      ...(valueTabNotificationCount > 0 ? { badge: valueTabNotificationCount } : {})
    },
    { key: "arb", icon: "⚖️", text: t.tabs.arb },
    { key: "middles", icon: "🎯", text: t.tabs.middles },
    { key: "notes", icon: "⚙️", text: t.tabs.notes }
  ], [valueTabNotificationCount, t]);

  const activeIndex = tabs.findIndex((t) => t.key === tab);
  const indicatorWidth = 100 / tabs.length;
  const planBadge = getPlanBadgeConfig(userPlan);
  const access = useMemo(() => ({
    steam: canAccessSteam(userPlan),
    arb: canAccessArb(userPlan),
    middles: canAccessMiddles(userPlan),
    value: canAccessValue(userPlan),
    notes: canAccessNotes(userPlan)
  }), [userPlan]);

  // #region agent log
  useEffect(() => {
    fetch('http://127.0.0.1:7867/ingest/2f6d3df5-a84c-436c-bfd4-cbb0456715a3',{method:'POST',headers:{'Content-Type':'application/json','X-Debug-Session-Id':'98bf9e'},body:JSON.stringify({sessionId:'98bf9e',location:'AnalysisLayout.jsx:mount',message:'AnalysisLayout mounted',data:{userPlan,accessSteam:canAccessSteam(userPlan),tab},timestamp:Date.now(),hypothesisId:'A'})}).catch(()=>{});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPlan]);
  // #endregion

  return (
    <div
      className="analysis-layout-shell"
      style={{
        display: "flex",
        flexDirection: "column",
        background: "#050816"
      }}
    >
      {tab === "steam" && (
        /* HEADER */
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "10px 12px",
            background:
              "linear-gradient(180deg, #101c3b 0%, #0b1630 100%)",
            borderBottom: "1px solid rgba(180, 197, 235, 0.10)",
            boxShadow: "0 8px 28px rgba(0, 0, 0, 0.32)",
            position: "relative",
            zIndex: 20
          }}
        >
          <div
            ref={langMenuRef}
            style={{
              position: "relative"
            }}
          >
            <button
              onClick={() => setLangMenuOpen((prev) => !prev)}
              style={{
                background: "#101c3b",
                color: "#f4f7ff",
                padding: "6px 10px",
                borderRadius: "6px",
                fontSize: "12px",
                border: "1px solid rgba(180, 197, 235, 0.10)",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                cursor: "pointer"
              }}
            >
              <FlagIcon item={currentLanguage} size={12} />
              <span>{currentLanguage.label}</span>
              <span
                style={{
                  fontSize: "10px",
                  color: "#7d89a8",
                  marginLeft: "2px"
                }}
              >
                ▼
              </span>
            </button>

            {langMenuOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 8px)",
                  left: 0,
                  minWidth: "210px",
                  maxHeight: "300px",
                  overflowY: "auto",
                  background: "#0b1630",
                  border: "1px solid rgba(180, 197, 235, 0.16)",
                  borderRadius: "10px",
                  boxShadow: "0 14px 40px rgba(0, 0, 0, 0.32)",
                  padding: "6px",
                  zIndex: 50
                }}
              >
                {languageOptions.map((item) => {
                  const active = item.code === lang;
                  return (
                    <button
                      key={item.code}
                      onClick={() => handleSelectLanguage(item.code)}
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        background: active
                          ? "rgba(76, 111, 255, 0.18)"
                          : "transparent",
                        color: active ? "#f4f7ff" : "#a8b3cf",
                        border: "none",
                        borderRadius: "8px",
                        padding: "9px 10px",
                        fontSize: "12px",
                        cursor: "pointer",
                        textAlign: "left"
                      }}
                    >
                      <FlagIcon item={item} size={12} />
                      <span>{item.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span
                style={{
                  fontSize: "12px",
                  color: "#a8b3cf",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: "220px"
                }}
              >
                {userEmail}
              </span>

              <span
                style={{
                  fontSize: "13px",
                  fontWeight: 600,
                  lineHeight: 1.2,
                  display: "inline-flex",
                  alignItems: "center",
                  flexShrink: 0,
                  letterSpacing: "0.15px",
                  color: planBadge.color,
                  whiteSpace: "nowrap"
                }}
              >
                {planBadge.label}
              </span>
            </div>

            <button
              onClick={handleLogout}
              style={{
                background: "#ff3b3b",
                color: "#fff",
                border: "none",
                padding: "6px 10px",
                borderRadius: "6px",
                fontSize: "12px",
                cursor: "pointer"
              }}
            >
              {t.header.logout}
            </button>
          </div>
        </div>
      )}

      {/* MAIN */}
      <div
        style={{
          flex: "1 1 auto",
          minHeight: 0,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          boxSizing: "border-box",
          paddingBottom: "calc(70px + env(safe-area-inset-bottom, 0px))"
        }}
      >
        <div style={{ display: tab === "steam" ? "block" : "none" }}>
          {access.steam
            ? <MemoAnalysisPage lang={lang} timeZone={timeZone} currentLanguage={currentLanguage} oddsFormat={oddsFormat} />
            : <LockedFeaturePanel message={getLocalizedLockedUpgradeMessage("premium", lang)} />}
        </div>

        <div style={{ display: tab === "arb" ? "block" : "none" }}>
          {access.arb && <MemoArbitragePage lang={lang} timeZone={timeZone} currentLanguage={currentLanguage} oddsFormat={oddsFormat} />}
        </div>

        <div style={{ display: tab === "value" ? "block" : "none" }}>
          {access.value
            ? <MemoValueBetsPage lang={lang} timeZone={timeZone} currentLanguage={currentLanguage} plan={userPlan} oddsFormat={oddsFormat} />
            : <LockedFeaturePanel message={getLocalizedLockedUpgradeMessage("pro", lang)} />}
        </div>

        <div style={{ display: tab === "middles" ? "block" : "none" }}>
          {access.middles && <MemoMiddlesPage lang={lang} timeZone={timeZone} currentLanguage={currentLanguage} oddsFormat={oddsFormat} />}
        </div>

        <div style={{ display: tab === "notes" ? "block" : "none" }}>
          {access.notes && <MemoAnalysisStatsPage lang={lang} timeZone={timeZone} currentLanguage={currentLanguage} userPlan={userPlan} />}
        </div>
      </div>

      {/* FOOTER */}
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          height: "calc(60px + env(safe-area-inset-bottom, 0px))",
          display: "flex",
          background:
            "linear-gradient(180deg, #101c3b 0%, #071226 100%)",
          borderTop: "1px solid rgba(180, 197, 235, 0.10)",
          boxShadow: "0 -10px 36px rgba(0, 0, 0, 0.32)",
          paddingBottom: "env(safe-area-inset-bottom, 0px)",
          paddingLeft: "env(safe-area-inset-left, 0px)",
          paddingRight: "env(safe-area-inset-right, 0px)",
          boxSizing: "border-box",
          flexShrink: 0
        }}
      >
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              flex: 1,
              background: "transparent",
              border: "none",
              color: tab === t.key ? "#f4f7ff" : "#7d89a8",
              fontSize: "11px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              transform: tab === t.key ? "scale(1.08)" : "scale(1)",
              transition: "color .18s ease, transform .18s ease",
              cursor: "pointer",
              padding: "0 2px",
              lineHeight: 1.1
            }}
          >
            <span
              style={{
                fontSize: "18px",
                marginBottom: "2px",
                transform: tab === t.key ? "scale(1.1)" : "scale(1)",
                transition: "transform .18s"
              }}
            >
              {t.icon}
            </span>

            <span
              style={{
                maxWidth: "100%",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis"
              }}
            >
              {t.text}
            </span>

            {typeof t.badge === "number" &&
              Number.isFinite(t.badge) &&
              t.badge > 0 && (
                <span
                  style={{
                    position: "absolute",
                    top: "6px",
                    right: "20px",
                    background: "#ff3b3b",
                    color: "#fff",
                    fontSize: "10px",
                    padding: "2px 5px",
                    borderRadius: "10px"
                  }}
                >
                  {t.badge}
                </span>
              )}
          </button>
        ))}

        <div
          style={{
            position: "absolute",
            bottom: 0,
            height: "3px",
            width: `${indicatorWidth}%`,
            left: `${indicatorWidth * activeIndex}%`,
            background: "#4c6fff",
            transition: "left .25s ease"
          }}
        />
      </div>
    </div>
  );
}
